import { zodResolver } from '@hookform/resolvers/zod'
import {
  type V2CreateAdminSocietyInput,
  V2CreateAdminSocietySchema
} from '@idc-forms/schemas/api-v2/admin-societies'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { useCompaniesV2LookupQuery } from '@/features/companies/hooks/use-companies-v2-lookup-query'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import { Button } from '@/shared/ui/button'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet
} from '@/shared/ui/field'
import { Input } from '@/shared/ui/input'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import { Switch } from '@/shared/ui/switch'

/**
 * En edición las credenciales SAP son OPCIONALES: la API nunca las devuelve, así
 * que el form arranca vacío y "vacío" significa "conservar las actuales" (el
 * schema v2 de update normaliza `''` a `undefined` antes del PATCH).
 */
const EditSocietyV2FormSchema = V2CreateAdminSocietySchema.extend({
  dbName: z.string().optional(),
  dbUser: z.string().optional(),
  dbPassword: z.string().optional()
})

export type SocietyV2FormValues = z.infer<typeof EditSocietyV2FormSchema>

type SocietyV2FormProps = {
  /** Alta (credenciales obligatorias) vs edición (vacías = conservar). */
  mode: 'create' | 'edit'
  /** Valores iniciales (edición) — ausente en creación. `company` es un `cmp_`. */
  defaultValues?: Partial<V2CreateAdminSocietyInput>
  submitLabel: string
  pending?: boolean
  onSubmit: (values: SocietyV2FormValues) => void
  onCancel: () => void
}

/**
 * Formulario de sociedad (crear/editar) para el side panel, contra la API v2.
 * Espeja el layout del form v1 pero cambia el transporte del campo empresa: el
 * select se alimenta del lookup v2 de empresas y su valor es un `cmp_`, no el
 * ObjectId. RHF + zodResolver con el MISMO schema v2 que valida el backend; en
 * edición las credenciales se relajan a opcionales (ver EditSocietyV2FormSchema).
 */
export function SocietyV2Form({
  mode,
  defaultValues,
  submitLabel,
  pending = false,
  onSubmit,
  onCancel
}: SocietyV2FormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<SocietyV2FormValues>({
    resolver: zodResolver(mode === 'create' ? V2CreateAdminSocietySchema : EditSocietyV2FormSchema),
    defaultValues: {
      active: true,
      ...defaultValues
    }
  })

  const { data: companies = [] } = useCompaniesV2LookupQuery()

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 px-3 py-3" noValidate>
      <FieldSet className="gap-4">
        <FieldLegend>Identificación</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.name)}>
                <FieldLabel htmlFor="society-name">Nombre</FieldLabel>
                <Input
                  id="society-name"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldDescription>Razón social completa, única entre sociedades.</FieldDescription>
                <FieldError errors={[errors.name]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="company"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.company)}>
                <FieldLabel htmlFor="society-company">Empresa</FieldLabel>
                <NativeSelect
                  id="society-company"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                  }}
                >
                  <NativeSelectOption value="" disabled>
                    Selecciona una empresa
                  </NativeSelectOption>
                  {companies.map((company) => (
                    <NativeSelectOption key={company.id} value={company.id}>
                      {company.name}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <FieldError errors={[errors.company]} />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <FieldSet className="gap-4">
        <FieldLegend>Credenciales SAP</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="dbName"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.dbName)}>
                <FieldLabel htmlFor="society-db-name">Base de datos</FieldLabel>
                <Input
                  id="society-db-name"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                {mode === 'edit' && (
                  <FieldDescription>
                    Déjalo vacío para conservar la credencial actual.
                  </FieldDescription>
                )}
                <FieldError errors={[errors.dbName]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="dbUser"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.dbUser)}>
                <FieldLabel htmlFor="society-db-user">Usuario</FieldLabel>
                <Input
                  id="society-db-user"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                {mode === 'edit' && (
                  <FieldDescription>
                    Déjalo vacío para conservar la credencial actual.
                  </FieldDescription>
                )}
                <FieldError errors={[errors.dbUser]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="dbPassword"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.dbPassword)}>
                <FieldLabel htmlFor="society-db-password">Contraseña</FieldLabel>
                <Input
                  id="society-db-password"
                  type="password"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="new-password"
                />
                <FieldDescription>
                  {mode === 'edit'
                    ? 'Déjala vacía para conservar la actual. Se guarda encriptada.'
                    : 'Se guarda encriptada; nadie puede volver a verla en claro.'}
                </FieldDescription>
                <FieldError errors={[errors.dbPassword]} />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <FieldSet className="gap-4">
        <FieldLegend>Estado</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="active"
            render={({ field }) => (
              <Field orientation="horizontal">
                <FieldLabel htmlFor="society-active">Activa</FieldLabel>
                <Switch
                  id="society-active"
                  checked={field.value ?? true}
                  onCheckedChange={field.onChange}
                />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <SidePanelActions
        actions={[
          {
            label: submitLabel,
            variant: 'default',
            disabled: pending,
            primary: true,
            onClick: () => {
              void handleSubmit(onSubmit)()
            }
          },
          {
            label: 'Cancelar',
            variant: 'outline',
            disabled: pending,
            onClick: onCancel
          }
        ]}
      />
      {/* El submit real va por SidePanelActions; este botón invisible habilita Enter. */}
      <Button type="submit" className="hidden" disabled={pending}>
        {submitLabel}
      </Button>
    </form>
  )
}
